import React, { useState, useEffect } from 'react';
import './Reviews.css';

// Initial reviews data with image paths
const INITIAL_REVIEWS = [
  {
    id: 1,
    text: 'Faheem worked with us on the CheckMyStaff app as a Product Designer and delivered excellent results. His UI/UX skills, attention to detail, and creative problem-solving significantly improved the user experience. He was professional, reliable, and consistently met deadlines. We highly recommend him for any product design projects.',
    name: 'Atif Arif',
    role: 'Founder & CTO @Logics Drive',
    linkedin: 'https://www.linkedin.com/in/atif-arif-00528058/',
    image: '/images/Review1.jfif',
    date: '2024-01-15'
  },
  {
    id: 2,
    text: 'Faheem has worked with us on multiple website design projects and social media marketing videos, delivering outstanding results every time. His creativity, technical skills, and understanding of branding helped elevate our online presence. He is professional, responsive, and always meets deadlines. We highly recommend him for web design and digital marketing projects.',
    name: 'Talha Khalid',
    role: 'Business Manager/PrimeDot LLC',
    linkedin: 'https://www.linkedin.com/in/talhabin-khalid/',
    image: '/images/Review2.jfif',
    date: '2024-01-16'
  },
  {
    id: 3,
    text: 'We had a great experience working with Faheem on animation projects for our social media reel ads. His creativity, motion skills, and attention to detail made our ads more engaging and impactful. He understood our brand vision perfectly and delivered high-quality work on time. Highly recommended for animation and social media content projects.',
    name: 'Digital Boss',
    role: 'Marketing Agency',
    linkedin: 'https://www.linkedin.com/company/dbosspk/',
    image: '/images/Review3.jfif',
    date: '2024-01-17'
  },
];

export default function Reviews() {
  const [reviews, setReviews] = useState(INITIAL_REVIEWS);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReview, setNewReview] = useState({
    name: '',
    role: '',
    text: '',
    linkedin: '',
    image: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // Load reviews from localStorage on mount
  useEffect(() => {
    const savedReviews = localStorage.getItem('reviews');
    if (savedReviews) {
      setReviews(JSON.parse(savedReviews));
    }
  }, []);

  // Save reviews to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('reviews', JSON.stringify(reviews));
  }, [reviews]);

  // Validate URL format
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!newReview.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!newReview.role.trim()) {
      errors.role = 'Role is required';
    }
    
    if (!newReview.text.trim()) {
      errors.text = 'Review text is required';
    }
    
    if (!newReview.linkedin.trim()) {
      errors.linkedin = 'LinkedIn URL is required';
    } else if (!isValidUrl(newReview.linkedin)) {
      errors.linkedin = 'Please enter a valid URL';
    } else if (!newReview.linkedin.includes('linkedin.com')) {
      errors.linkedin = 'Please enter a valid LinkedIn URL';
    }
    
    if (!newReview.image.trim()) {
      errors.image = 'Image URL is required';
    } else if (!isValidUrl(newReview.image)) {
      errors.image = 'Please enter a valid image URL';
    }
    
    return errors;
  };

  // Handle adding new review
  const handleAddReview = (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    const reviewToAdd = {
      ...newReview,
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
    };
    
    setReviews([...reviews, reviewToAdd]);
    setNewReview({ name: '', role: '', text: '', linkedin: '', image: '' });
    setFormErrors({});
    setShowAddForm(false);
  };

  // Get initials from name (for fallback)
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <section className="reviews section" id="reviews">
      <div className="container">
        <div className="text-center">
          <span className="section-label">REVIEW</span>
          <h2 className="section-title">CLIENT REVIEWS</h2>
          <div className="divider" />
        </div>

        {/* Add Review Button */}
        <div className="reviews-header">
          <button 
            className="add-review-btn"
            onClick={() => {
              setShowAddForm(!showAddForm);
              setFormErrors({});
            }}
          >
            {showAddForm ? 'Cancel' : '+ Add Review'}
          </button>
        </div>

        {/* Add Review Form */}
        {showAddForm && (
          <div className="add-review-form">
            <h3>Add New Review</h3>
            <p className="form-required-note">All fields are required</p>
            <form onSubmit={handleAddReview}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  placeholder="Enter full name"
                  value={newReview.name}
                  onChange={(e) => {
                    setNewReview({...newReview, name: e.target.value});
                    setFormErrors({...formErrors, name: ''});
                  }}
                  className={formErrors.name ? 'error' : ''}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>
              
              <div className="form-group">
                <label>Role/Position *</label>
                <input
                  type="text"
                  placeholder="Enter role (e.g., Chairman, CEO)"
                  value={newReview.role}
                  onChange={(e) => {
                    setNewReview({...newReview, role: e.target.value});
                    setFormErrors({...formErrors, role: ''});
                  }}
                  className={formErrors.role ? 'error' : ''}
                />
                {formErrors.role && <span className="error-message">{formErrors.role}</span>}
              </div>
              
              <div className="form-group">
                <label>Review Text *</label>
                <textarea
                  placeholder="Enter review text"
                  value={newReview.text}
                  onChange={(e) => {
                    setNewReview({...newReview, text: e.target.value});
                    setFormErrors({...formErrors, text: ''});
                  }}
                  rows="4"
                  className={formErrors.text ? 'error' : ''}
                />
                {formErrors.text && <span className="error-message">{formErrors.text}</span>}
              </div>
              
              <div className="form-group">
                <label>LinkedIn Profile URL *</label>
                <input
                  type="url"
                  placeholder="https://linkedin.com/in/username"
                  value={newReview.linkedin}
                  onChange={(e) => {
                    setNewReview({...newReview, linkedin: e.target.value});
                    setFormErrors({...formErrors, linkedin: ''});
                  }}
                  className={formErrors.linkedin ? 'error' : ''}
                />
                {formErrors.linkedin && <span className="error-message">{formErrors.linkedin}</span>}
              </div>
              
              <div className="form-group">
                <label>Profile Image URL *</label>
                <input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={newReview.image}
                  onChange={(e) => {
                    setNewReview({...newReview, image: e.target.value});
                    setFormErrors({...formErrors, image: ''});
                  }}
                  className={formErrors.image ? 'error' : ''}
                />
                {formErrors.image && <span className="error-message">{formErrors.image}</span>}
                <small className="form-hint">Use a square image for best results</small>
              </div>
              
              {/* Image Preview */}
              {newReview.image && isValidUrl(newReview.image) && (
                <div className="image-preview">
                  <p>Image Preview:</p>
                  <img 
                    src={newReview.image} 
                    alt="Preview" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/100?text=Error';
                    }}
                  />
                </div>
              )}
              
              <button type="submit" className="submit-review-btn">Add Review to Portfolio</button>
            </form>
          </div>
        )}

        {/* Reviews Grid with Bubble Design */}
        <div className="reviews-bubble-grid">
          {reviews.map((review) => (
            <div className="review-bubble-card" key={review.id}>
              {/* Message Bubble */}
              <div className="review-bubble">
                <p className="review-bubble-text">"{review.text}"</p>
                <div className="bubble-arrow"></div>
              </div>
              
              {/* Profile Section */}
              <div className="review-profile">
                {/* Clickable Image inside Link */}
                <a 
                  href={review.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="review-avatar-link"
                  title={`View ${review.name}'s LinkedIn Profile`}
                >
                  <img 
                    src={review.image} 
                    alt={review.name}
                    className="review-avatar-img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${review.name.replace(' ', '+')}&size=200&background=random`;
                    }}
                  />
                </a>
                
                {/* Name and Role */}
                <div className="review-profile-info">
                  <h3 className="review-name-bubble">{review.name}</h3>
                  <p className="review-role-bubble">{review.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}